import React from 'react';
import classNames, { Argument } from 'classnames';
import { FormattedMessage } from 'react-intl';

import { computeDiscountedPrice, computeTax } from 'utils/priceUtils';

import { AbstractMedia } from 'types/AbstractMedia';

import { isPublicDevice } from 'modules/dealers/selectors';

import { useSelector } from 'hooks';
import FileList from 'components/ui/FileList';
import InclTaxLabel from 'components/ui/InclTaxLabel';
import useLabelTranslation from 'hooks/useLabelTranslation';
import { getPayerById } from 'modules/selfServices/selectors';
import { RepairOrder } from 'modules/selfServices/types/SelfService';
import { usePriceFormatter } from 'utils/PriceFormatter/usePriceFormatter';

const TRUNCATE_LIMIT = 220;

export interface RepairOrderItemProps extends RepairOrder {
  className?: Argument;
  medias?: AbstractMedia[];
}
const RepairOrderItem: React.FC<RepairOrderItemProps> = ({
  className,
  coefficient,
  discountedPrice,
  medias,
  payerId,
  price,
  translatedDescription: description,
  translatedName: name,
  vat,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const isPublic = useSelector(isPublicDevice);
  const payer = useSelector((state) => getPayerById(state, payerId));

  const { getLabelTranslation } = useLabelTranslation();
  const priceFormatter = usePriceFormatter();

  const showPayer = Boolean(payer);
  const showPrice = discountedPrice >= 0;
  const showDiscount = price !== discountedPrice;
  const isTruncated = description?.length > TRUNCATE_LIMIT;

  const toggleDescription = React.useCallback(() => setIsExpanded((currentIsExpanded) => !currentIsExpanded), []);

  return (
    <div className={classNames('flex flex-col', className)}>
      <div className="flex justify-between items-center gap-2">
        <div data-testid="repair-order-name" className="text-default font-bold kiosk:text-3xl flex flex-wrap">
          {name}
        </div>
        {showPrice && (
          <div className="flex flex-col justify-start items-end">
            <p className="font-bold text-default" data-testid="repair-order-discountedPrice">
              {priceFormatter(computeDiscountedPrice({ price, discountedPrice, coefficient }, vat))}
            </p>
            <InclTaxLabel />
            {showDiscount && (
              <p className="line-through italic font-medium text-s" data-testid="repair-order-price">
                {priceFormatter(computeTax(price, vat, coefficient))}
              </p>
            )}
          </div>
        )}
      </div>
      {showPayer && (
        <span
          className="text-right text-lowest italic font-semibold text-sm kiosk:text-2xl kioskSharebox:text-xl truncate"
        >
          <FormattedMessage
            id="billing.payerLabel"
            defaultMessage="Billing: {payerName}"
            values={{ payerName: getLabelTranslation(payer.name) }}
          />
        </span>
      )}
      {Boolean(description) && (
        <div
          data-testid="repair-order-description"
          className="text-low text-sm kiosk:text-2xl kioskSharebox:text-xl mt-2"
        >
          {isExpanded || !isTruncated ? description : `${description?.slice(0, TRUNCATE_LIMIT)}...`}
          {isTruncated && (
            <span
              className="underline text-s cursor-pointer ml-1"
              onClick={toggleDescription}
              data-testid="expand-description"
            >
              {isExpanded ? (
                <FormattedMessage id="expand.less" defaultMessage="View less" />
              ) : (
                <FormattedMessage id="expand.more" defaultMessage="View more" />
              )}
            </span>
          )}
        </div>
      )}
      {medias?.length > 0 && <FileList medias={medias} showDownload={!isPublic} className="mt-2" />}
    </div>
  );
};

export default RepairOrderItem;
